/**
 * Tiny promise wrapper around IndexedDB used by the learner offline cache.
 * Writes resolve only after the transaction completes so a refresh cannot
 * drop a queued API that `add`/`put` already acknowledged.
 */
import { IDB_NAME, IDB_VERSION, STORE_GQL, STORE_SESSION, STORE_SYNC } from "./constants";

let dbPromise = null;

/**
 * Opens (and upgrades) the offline database. Reuses one connection.
 *
 * @returns {Promise<IDBDatabase>}
 */
const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const from = event.oldVersion || 0;
      // Drop GraphQL + session snapshots on upgrade so stale SCORM outlines
      // (VAR_SCORM_HASH) cannot keep painting after the API embeds real hashes.
      if (from > 0 && from < 3) {
        if (db.objectStoreNames.contains(STORE_GQL)) db.deleteObjectStore(STORE_GQL);
        if (db.objectStoreNames.contains(STORE_SESSION)) db.deleteObjectStore(STORE_SESSION);
      }
      if (!db.objectStoreNames.contains(STORE_GQL)) {
        db.createObjectStore(STORE_GQL, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORE_SYNC)) {
        db.createObjectStore(STORE_SYNC, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_SESSION)) {
        db.createObjectStore(STORE_SESSION, { keyPath: "key" });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
    req.onblocked = () => {
      /* another tab is upgrading; the next call reopens */
    };
  });
  return dbPromise;
};

/**
 * Runs a read or write against one object store and waits until it is durable.
 *
 * @param {string} storeName Store name.
 * @param {"readonly"|"readwrite"} mode Transaction mode.
 * @param {(store: IDBObjectStore) => IDBRequest} run Request factory.
 * @returns {Promise<*>} Request result.
 */
const withStore = async (storeName, mode, run) => {
  const db = await openDb();
  if (!db.objectStoreNames.contains(storeName)) {
    throw new Error(`missing idb store ${storeName}`);
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const req = run(store);
    let result;
    req.onsuccess = () => {
      result = req.result;
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve(result);
    tx.onabort = () => reject(tx.error || new Error("idb abort"));
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * Puts a record into a store.
 *
 * @param {string} storeName Store name.
 * @param {object} value Record to write.
 * @returns {Promise<*>}
 */
export const idbPut = (storeName, value) => withStore(storeName, "readwrite", (store) => store.put(value));

/**
 * Reads a record by key.
 *
 * @param {string} storeName Store name.
 * @param {IDBValidKey} key Primary key.
 * @returns {Promise<*|undefined>}
 */
export const idbGet = (storeName, key) => withStore(storeName, "readonly", (store) => store.get(key));

/**
 * Deletes a record by key.
 *
 * @param {string} storeName Store name.
 * @param {IDBValidKey} key Primary key.
 * @returns {Promise<*>}
 */
export const idbDelete = (storeName, key) => withStore(storeName, "readwrite", (store) => store.delete(key));

/**
 * Inserts a record and returns the generated key (autoIncrement stores).
 *
 * @param {string} storeName Store name.
 * @param {object} value Record to insert.
 * @returns {Promise<IDBValidKey>}
 */
export const idbAdd = (storeName, value) => withStore(storeName, "readwrite", (store) => store.add(value));

/**
 * Reads every record in a store.
 *
 * @param {string} storeName Store name.
 * @returns {Promise<Array>}
 */
export const idbGetAll = async (storeName) => {
  const rows = await withStore(storeName, "readonly", (store) => store.getAll());
  return Array.isArray(rows) ? rows : [];
};

/**
 * Removes every record from a store.
 *
 * @param {string} storeName Store name.
 * @returns {Promise<void>}
 */
export const idbClear = (storeName) => withStore(storeName, "readwrite", (store) => store.clear());
